import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { getCdnImageBaseUrl } from '@susu/custom-made/utils/imageDefinition';
import { CARD_LIST_BASE_STYLE_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { useI18N } from '@susu/i18n/client';

const getBaseStyleClasses = (isPresentationOnly: boolean) =>
  cn('group', {
    'cursor-pointer pb-px lg:relative lg:flex lg:h-[68px] lg:pt-0 lg:pr-32 lg:pb-px lg:pl-10 lg:items-center lg:gap-12 lg:self-stretch lg:rounded-[60px] lg:bg-gray-200 lg:m-[11px] 2xl:m-[13px] 2xl:h-[76px]':
      !isPresentationOnly,
    'relative flex items-center gap-12 self-stretch py-6 px-0 lg:gap-20 lg:p-0 lg:h-auto': isPresentationOnly,
  });

const getBaseStyleContainerClasses = (isPresentationOnly: boolean) =>
  cn('flex justify-center items-center shrink-0 bg-gray-200 rounded-md cursor-pointer', {
    'w-56 h-[88px] m-3 md:w-[76px] md:h-[116px] md:m-[5px] lg:m-0 lg:rounded-[52px] lg:bg-white lg:w-48 lg:h-48 lg:p-12 2xl:w-56 2xl:h-56':
      !isPresentationOnly,
    'w-[64px] h-[64px] m-0 md:w-[100px] md:h-[100px] lg:w-80 lg:h-80 lg:p-12 lg:bg-gray-300 2xl:w-[100px] 2xl:h-[100px]':
      isPresentationOnly,
  });

const getBaseStyleImageClasses = (isPresentationOnly: boolean) =>
  isPresentationOnly ? 'w-28 h-28' : 'w-24 h-24 md:w-28 md:h-28';

const getBaseStyleTextClasses = (isPresentationOnly: boolean) =>
  cn('overflow-hidden text-ellipsis text-gray-800 font-medium', {
    'text-center text-[11px] leading-[14px] pt-8 md:text-sm lg:text-left lg:text-[13px] lg:leading-xs lg:pt-0 lg:group-hover:text-gray-600 2xl:text-sm 2xl:leading-sm 2xl:pb-[2px]':
      !isPresentationOnly,
    'text-left text-sm leading-sm p-0 md:text-base md:leading-base': isPresentationOnly,
  });

const getBaseStyleDescriptionClasses = (isPresentationOnly: boolean) =>
  isPresentationOnly
    ? 'block text-sm font-light leading-sm lg:text-gray-800'
    : 'hidden lg:block lg:text-gray-800 lg:text-[13px] lg:font-light lg:leading-sm 2xl:text-sm';

const getArrowClasses = (isPresentationOnly: boolean) =>
  isPresentationOnly
    ? 'hidden'
    : 'flex justify-center items-center shrink-0 w-6 h-[88px] mt-3 mr-[2px] mb-[25px] ml-[5px] md:h-[116px] md:mt-[5px] md:mb-[27px] lg:hidden';

export default function CardListBaseStyle({
  isPresentationOnly,
  baseStyleClick,
  stylePresetSelectedValue,
}: Readonly<{
  isPresentationOnly?: boolean;
  baseStyleClick?: () => void;
  stylePresetSelectedValue: () => string | null;
}>) {
  const { t } = useI18N();
  const { cdnDomain } = useEnvironmentContext();
  const cdnImageBaseUrl = getCdnImageBaseUrl(cdnDomain);
  const presentation = !!isPresentationOnly;

  return (
    <>
      <div
        className={getBaseStyleClasses(presentation)}
        data-testid={CARD_LIST_BASE_STYLE_TEST_IDS.CARD}
        onClick={baseStyleClick ?? (() => {})}
        onKeyDown={
          baseStyleClick
            ? e => {
                onEnter(e, () => {
                  baseStyleClick();
                });
              }
            : () => {}
        }
      >
        <div className={getBaseStyleContainerClasses(presentation)}>
          <img
            className={getBaseStyleImageClasses(presentation)}
            src={`${cdnImageBaseUrl}/svg/base_style.svg`}
            alt={t('cardListBaseStyle.imageAlt')}
            height={24}
            width={24}
          />
        </div>
        <div>
          <div className={getBaseStyleTextClasses(presentation)}>{t('cardListBaseStyle.yourStyle')}</div>
          <div
            className={getBaseStyleDescriptionClasses(presentation)}
            data-testid={CARD_LIST_BASE_STYLE_TEST_IDS.DESCRIPTION}
          >
            {stylePresetSelectedValue()}
          </div>
        </div>
        <div className={cn('hidden', { 'lg:block lg:absolute lg:top-[calc(50%-10px)] lg:right-32 lg:w-20 lg:h-20': !presentation })}>
          <i className="susu-icons text-gray-600 lg:text-[20px]" translate="no">
            edit
          </i>
        </div>
      </div>
      <div className={getArrowClasses(presentation)}>
        <i className="susu-icons text-gray-500 text-[24px] md:text-[28px]" translate="no">
          slight_arrow_right
        </i>
      </div>
    </>
  );
}
